/**
 * Operation Run Renderer
 *
 * Covers the whole `Operation:<operationType>:<phase>` family (TV1-2113 D12) —
 * bulk publish, reprice, kit inference, and every operation type added after this
 * file was written.
 *
 * These names are minted at PROJECTION time from the run's `operationType` and
 * `phase`, so they can never match a registry keyed by literal string. Before this
 * renderer existed, every operation run in the batch history rendered as an
 * untitled grey question mark — the "batch operations with no title" defect.
 *
 * Resolution is by PREFIX (`parseOperationRunName`), not by enumeration, so a new
 * `OperationTypeSchema` value gets a correctly-shaped, mostly-translated row on the
 * day it ships rather than falling back to "unknown".
 *
 * Data shape (from `operationBatchProcessStartedProjector`):
 *   operationType: string, phase: 'compute' | 'apply', operationRunId: string
 */

import { FC } from 'react';
import { Stack, Text } from '@mantine/core';
import { parseOperationRunName } from '../../../constants';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

/**
 * Translate one raw server token (an operation type or a phase) through the locale
 * files, defaulting to the token itself. Shared by the summary and the detail body so
 * the two cannot disagree.
 */
const translateToken = (
  prefix: string,
  token: string | undefined,
  t: DetailRendererProps['t']
): string => ( token ? t( `${prefix}${token}`, { defaultValue: token }) : '' );

const DetailComponent: FC<DetailRendererProps> = ({ process, t }) => {
  const data = ( process.data || {}) as ProcessData;
  // Prefer the projected fields; fall back to re-parsing the name for a row
  // projected before those fields were carried.
  const parsed = parseOperationRunName( process.operationName ?? '' );
  const operationType = ( data.operationType as string ) ?? parsed?.operationType;
  const phase = ( data.phase as string ) ?? parsed?.phase;

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t( `batchProcesses_operations_phases_${phase}_description`, {
          defaultValue: t( 'batchProcesses_operations_operationRun_description' )
        })}
      </Text>

      <Stack gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_operation' )}:
        </Text>
        <Text fz="sm">
          {translateToken( 'batchProcesses_operations_operationTypes_', operationType, t )}
        </Text>
      </Stack>
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t, process ) => {
    const parsed = parseOperationRunName( process?.operationName ?? '' );
    const operationType = ( data?.operationType as string ) ?? parsed?.operationType;
    return (
      translateToken( 'batchProcesses_operations_operationTypes_', operationType, t ) ||
      t( 'batchProcesses_summary_unknownOperation' )
    );
  },
  DetailComponent,
  getRequiredReferenceIds: () => ({})
};

export default DetailComponent;
